const CODE_RE = /[?&]code=[^&]+/;
const STATE_RE = /[?&]state=[^&]+/;
const ERROR_RE = /[?&]error=[^&]+/;

export const hasAuthParams = (searchParams = window.location.search) =>
  (CODE_RE.test(searchParams) || ERROR_RE.test(searchParams)) &&
  STATE_RE.test(searchParams);

export const encode = (value) => btoa(value);

export const getCrypto = () => {
  //ie 11.x uses msCrypto
  return window.crypto || window.msCrypto;
};

export const createRandomString = () => {
  const charset =
    '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz-_~.';
  let random = '';
  const randomValues = Array.from(
    getCrypto().getRandomValues(new Uint8Array(43))
  );
  randomValues.forEach((v) => (random += charset[v % charset.length]));
  return random;
};

export const sha256 = async (s) => {
  const digestOp = getCryptoSubtle().digest(
    { name: 'SHA-256' },
    new TextEncoder().encode(s)
  );

  // msCrypto (IE11) uses the old spec, which is not Promise based
  // https://msdn.microsoft.com/en-us/expression/dn904640(v=vs.71)
  // Instead of returning a promise, it returns a CryptoOperation
  // with a result property in it.
  // As a result, the various events need to be handled in the event that we're
  // working in IE11 (hence the msCrypto check). These events just call resolve
  // or reject depending on their intention.
  if (window.msCrypto) {
    return new Promise((res, rej) => {
      digestOp.oncomplete = (e) => {
        res(e.target.result);
      };

      digestOp.onerror = (e) => {
        rej(e.error);
      };

      digestOp.onabort = () => {
        rej('The digest operation was aborted');
      };
    });
  }

  return await digestOp;
};

export const getCryptoSubtle = () => {
  const crypto = getCrypto();
  //safari 10.x uses webkitSubtle
  return crypto.subtle || crypto.webkitSubtle;
};

export const bufferToBase64UrlEncoded = (input) => {
  const ie11SafeInput = new Uint8Array(input);
  return urlEncodeB64(
    window.btoa(String.fromCharCode(...Array.from(ie11SafeInput)))
  );
};

const urlEncodeB64 = (input) => {
  const b64Chars = { '+': '-', '/': '_', '=': '' };
  return input.replace(/[+/=]/g, (m) => b64Chars[m]);
};

export const parseQueryResult = (queryString) => {
  if (queryString.indexOf('#') > -1) {
    queryString = queryString.substr(0, queryString.indexOf('#'));
  }

  const queryParams = queryString.split('&');

  const parsedQuery = {};
  queryParams.forEach((qp) => {
    const [key, val] = qp.split('=');
    parsedQuery[key] = decodeURIComponent(val);
  });

  return {
    ...parsedQuery,
  };
};

/**
 * Detect if an error is an authentication/session error
 * Checks for HTTP status codes (401, 403) and various error message patterns
 * @param {Error} error - The error object to check
 * @returns {boolean} - True if this is an auth/session error
 */
export const isAuthError = (error) => {
  const errorMessage = (error?.message || '').toLowerCase();

  return (
    error?.response?.status === 401 ||
    error?.response?.status === 403 ||
    errorMessage.includes('authentication') ||
    errorMessage.includes('refresh_token_expired') ||
    errorMessage.includes('token') ||
    errorMessage.includes('session') ||
    errorMessage.includes('unauthorized') ||
    errorMessage.includes('forbidden')
  );
};

/**
 * Clean up multiple sessionStorage keys at once
 * @param {string[]} keys - Array of sessionStorage keys to remove
 * @param {string} context - Optional context for logging (e.g., 'ComponentForm restoration')
 */
export const cleanupSessionStorage = (keys, context = '') => {
  keys.forEach((key) => {
    sessionStorage.removeItem(key);
  });
};

/**
 * Show a standardized restoration alert message
 * @param {Object} options - Alert configuration options
 * @param {string} options.formType - Type of form being restored (e.g., 'component', 'template', 'course list')
 * @param {boolean} options.includeTemplate - Whether template data was also restored
 * @param {string} options.customMessage - Optional custom message to append
 */
export const showRestorationAlert = ({
  formType,
  includeTemplate = false,
  customMessage = '',
}) => {
  const baseMessage = includeTemplate
    ? `⚠️ Your previous ${formType} AND template changes have been restored.`
    : `⚠️ Your previous ${formType} changes have been restored.`;

  const warningMessage = '⚠️ IMPORTANT: These changes are NOT saved yet!';

  const actionMessage = includeTemplate
    ? 'Please review your changes and click SAVE on both the component and template to persist them to the database.'
    : 'Please review your changes and click SAVE to persist them to the database.';

  const fullMessage = [
    baseMessage,
    '',
    warningMessage,
    '',
    actionMessage,
    customMessage ? `\n${customMessage}` : '',
  ].join('\n');

  alert(fullMessage);
};

/**
 * Get current user from JWT token for identity verification
 * @returns {string|null} - User email or null if not authenticated
 */
export const getCurrentUserFromToken = () => {
  try {
    const token = localStorage.getItem('pat.jwt.token');
    if (!token) return null;

    // Simple decode without verification (we trust it's from our auth flow)
    const base64Url = token.split('.')[1];
    if (!base64Url) return null;

    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );

    const decoded = JSON.parse(jsonPayload);
    return decoded.email || decoded.sub || null;
  } catch (error) {
    console.error('[getCurrentUserFromToken] Error decoding token:', error);
    return null;
  }
};

/**
 * Parse saved form data from sessionStorage with user identity verification
 * @param {string} key - SessionStorage key to retrieve
 * @param {Object} defaultValue - Default value if parsing fails or data doesn't exist
 * @returns {Object|null} - Parsed data or defaultValue
 */
export const parseSessionData = (key, defaultValue = null) => {
  try {
    const savedData = sessionStorage.getItem(key);
    if (!savedData) {
      return defaultValue;
    }

    const parsedData = JSON.parse(savedData);

    // Verify user identity if metadata exists
    if (parsedData._savedByUser) {
      const currentUser = getCurrentUserFromToken();

      if (currentUser && parsedData._savedByUser !== currentUser) {
        console.warn(
          `[parseSessionData] Data in '${key}' belongs to different user`,
          '\n  Saved by:',
          parsedData._savedByUser,
          '\n  Current user:',
          currentUser,
          '\n  Clearing stale data to prevent cross-user contamination'
        );
        sessionStorage.removeItem(key);
        return defaultValue;
      }

      console.log(
        `[parseSessionData] Verified user identity for '${key}':`,
        parsedData._savedByUser
      );

      // Remove internal metadata fields before returning
      const { _savedByUser, _savedAt, ...cleanData } = parsedData;
      return cleanData;
    }

    return parsedData;
  } catch (error) {
    console.error(
      `[parseSessionData] Error parsing data for key ${key}:`,
      error
    );
    return defaultValue;
  }
};
